import {
  buildFocusItems,
  type FocusInput,
} from '../daily-brief/focusRules';
import type { FocusItem, FocusSeverity } from '../daily-brief/types';
import type { OpsActionItem, OpsIntelligenceModel, OpsPriority } from './types';

const DESCRIPTIONS: Record<string, string> = {
  'conn-bad': 'Restore a healthy seller connection before relying on commerce metrics.',
  'orders-fetch-fail': 'Retry LIVE orders load or confirm Connect credentials and domain health.',
  'orders-quality': 'Inspect domain quality on Connect and re-run orders sync if needed.',
  'sync-stale': 'Trigger or wait for a successful orders sync so the dashboard reflects reality.',
  'gmv-critical': 'Investigate demand, listing, or pricing changes behind the sales drop.',
  'gmv-warn': 'Compare order mix and totals against the prior day for the same connection.',
  'gmv-up': 'Confirm capacity and inventory can support elevated sales.',
  'orders-critical': 'Validate whether the drop is real demand or a data window coverage issue.',
  'orders-warn': 'Review funnel and open-order pipeline for the window.',
  'orders-up': 'Watch fulfillment load and stock as volume rises.',
  unshipped: 'Clear open Amazon statuses that risk SLA pressure.',
  'cancel-rate': 'Identify cancel causes (stock, price, buyer) from order headers.',
  'zero-stock': 'Prioritize restock or suppress zero-stock SKUs in the inventory sample.',
  'no-orders-window': 'Confirm time zone, connection, and that MarketSync holds recent headers.',
  'gmv-missing': 'Backfill or wait for OrderTotal coverage; do not mix multi-currency GMV.',
  'scan-truncated': 'Treat totals as partial until a fuller certified window is available.',
  'no-connection': 'Select or authorize an Amazon connection to generate ops signals.',
};

const DOMAIN: Record<string, OpsActionItem['sourceDomain']> = {
  'conn-bad': 'connect',
  'no-connection': 'connect',
  'orders-fetch-fail': 'orders',
  'orders-quality': 'sync',
  'sync-stale': 'sync',
  'gmv-critical': 'orders',
  'gmv-warn': 'orders',
  'gmv-up': 'orders',
  'orders-critical': 'orders',
  'orders-warn': 'orders',
  'orders-up': 'orders',
  unshipped: 'orders',
  'cancel-rate': 'orders',
  'zero-stock': 'inventory',
  'no-orders-window': 'orders',
  'gmv-missing': 'orders',
  'scan-truncated': 'orders',
};

function confidenceFor(item: FocusItem, truncated: boolean, hasInventorySample: boolean): number {
  let c = 72;
  if (item.id === 'conn-bad' || item.id === 'no-connection') c = 95;
  if (item.id === 'orders-fetch-fail') c = 90;
  if (item.id.startsWith('gmv') || item.id.startsWith('orders')) c = truncated ? 48 : 70;
  if (item.id === 'zero-stock') c = hasInventorySample ? 55 : 0;
  if (item.id === 'scan-truncated') c = 40;
  if (item.id === 'sync-stale' || item.id === 'orders-quality') c = 80;
  if (item.severity === 'opportunity') c = Math.min(c, 68);
  return Math.max(0, Math.min(100, c));
}

function toPriority(s: FocusSeverity): OpsPriority {
  return s;
}

/**
 * Deterministic ops rule engine — wraps certified Daily Brief focus rules.
 * No AI. Items without evidence never leave buildFocusItems.
 */
export function buildOpsActions(
  input: FocusInput & {
    loadedAt: string;
    truncated: boolean;
  },
): OpsActionItem[] {
  const focus = buildFocusItems(input);
  const hasInv = input.inventorySample > 0;
  return focus
    .map((item) => ({
      id: item.id,
      priority: toPriority(item.severity),
      title: item.title,
      description: DESCRIPTIONS[item.id] ?? 'Review evidence and open the linked workspace.',
      evidence: item.evidence,
      timestamp: input.loadedAt,
      confidence: confidenceFor(item, input.truncated, hasInv),
      sourceDomain: DOMAIN[item.id] ?? 'orders',
      href: item.href,
    }))
    .filter((i) => i.evidence.trim().length > 0 && i.confidence > 0);
}

export function noConnectionItem(loadedAt: string): OpsActionItem {
  return {
    id: 'no-connection',
    priority: 'critical',
    title: 'No Amazon connection selected',
    description: DESCRIPTIONS['no-connection'],
    evidence: 'Open Connect to authorize a seller account.',
    timestamp: loadedAt,
    confidence: 95,
    sourceDomain: 'connect',
    href: '/amazon/connect',
  };
}

export function buildOpsModel(opts: {
  connectionId: string | null;
  sellerRef?: string;
  focusInput: FocusInput | null;
  truncated: boolean;
  error: string | null;
  source?: string;
}): OpsIntelligenceModel {
  const loadedAt = new Date().toISOString();

  if (!opts.focusInput || !opts.connectionId) {
    return {
      loadedAt,
      connectionId: opts.connectionId,
      sellerRef: opts.sellerRef,
      items: [noConnectionItem(loadedAt)],
      truncated: false,
      error: opts.error,
      sourceNote:
        'Operations Intelligence uses certified LIVE order/inventory/connect signals only. No invented alerts.',
    };
  }

  const items = buildOpsActions({
    ...opts.focusInput,
    loadedAt,
    truncated: opts.truncated,
  });

  return {
    loadedAt,
    connectionId: opts.connectionId,
    sellerRef: opts.sellerRef,
    items,
    truncated: opts.truncated,
    error: opts.error,
    sourceNote: opts.source
      ? `Rules over LIVE reads (source=${opts.source}). Evidence-backed items only.`
      : 'Deterministic rules over certified LIVE domains. Unavailable domains produce no alerts.',
  };
}
